'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { colors, baseStyles, formatUSD, formatNum, formatSmartPrice, DeltaBadge } from './SharedUI';
import HistoricalSection from './HistoricalSection';
import LiquidationHeatmapAnalyzer from './LiquidationHeatmapAnalyzer';
import MultiExchangeSentimentAnalyzer from './MultiExchangeSentimentAnalyzer';
import OrderBookAnalyzer from './OrderBookAnalyzer';

export default function LiveMarketSection({ symbol, liveDataRef, smartMoneyRef }) {
  const [liveData, setLiveData] = useState(null);
  const [baseline, setBaseline] = useState(null);
  const baselineRef = useRef(null);
  
  const [dataSource, setDataSource] = useState('stream'); 
  const [tempSource, setTempSource] = useState('stream');
  
  // State theo dõi kết nối & Kiểm toán
  const [connStatus, setConnStatus] = useState({ text: '⚪ CHƯA KẾT NỐI', color: colors.textSub });
  const [serverSubscriptions, setServerSubscriptions] = useState(null);

  const wsRef = useRef(null);
  const crawlerTimerRef = useRef(null);
  const activeStreamsRef = useRef([]);

  const fetchExtraData = useCallback(async () => {
    try {
      const currentSymbol = symbol.toUpperCase();
      const [oiRes, fundRes, priceRes, klineRes] = await Promise.all([
        fetch(`https://fapi.binance.com/fapi/v1/openInterest?symbol=${currentSymbol}`),
        fetch(`https://fapi.binance.com/fapi/v1/premiumIndex?symbol=${currentSymbol}`),
        fetch(`https://fapi.binance.com/fapi/v1/ticker/24hr?symbol=${currentSymbol}`),
        fetch(`https://fapi.binance.com/fapi/v1/klines?symbol=${currentSymbol}&interval=1d&limit=1`)
      ]);
      
      const oiJson = await oiRes.json();
      const fundJson = await fundRes.json();
      const priceJson = await priceRes.json();
      const klineJson = await klineRes.json();

      if (currentSymbol !== symbol.toUpperCase()) return;

      const lastPrice = parseFloat(priceJson.lastPrice);
      const oiContract = oiJson.openInterest ? parseFloat(oiJson.openInterest) : 0;
      
      let cvdValue = 0;
      if (klineJson && klineJson[0]) {
        const vol = parseFloat(klineJson[0][7]);
        const takerBuy = parseFloat(klineJson[0][10]);
        cvdValue = takerBuy - (vol - takerBuy);
      }

      const updates = {
        symbol: currentSymbol,
        last: lastPrice,
        open: parseFloat(priceJson.openPrice),
        high: parseFloat(priceJson.highPrice),
        low: parseFloat(priceJson.lowPrice),
        changePct: parseFloat(priceJson.priceChangePercent),
        vol: parseFloat(priceJson.quoteVolume),
        oiContract: oiContract,
        oiQuote: oiContract * lastPrice,
        cvd: cvdValue,
        fundingRate: fundJson.lastFundingRate ? parseFloat(fundJson.lastFundingRate) : 0
      };

      setLiveData(updates);
      if (!baselineRef.current) { baselineRef.current = updates; setBaseline(updates); }
      if (liveDataRef) liveDataRef.current = updates;
    } catch (e) { console.error("Crawler Error:", e); }
  }, [symbol, liveDataRef]);

  const startStream = useCallback(() => {
    const streamName = symbol.toLowerCase();
    const streamsToSub = [
      `${streamName}@ticker`, 
      `${streamName}@kline_1d`, 
      `${streamName}@markPrice`
    ];
    
    setConnStatus({ text: '⏳ ĐANG MỞ KẾT NỐI...', color: colors.warn });

    const handleMessage = (event) => {
      const payload = JSON.parse(event.data);
      
      // 1. Phản hồi Audit
      if (Array.isArray(payload.result) && payload.id === 2) {
          setServerSubscriptions(payload.result);
          return;
      }

      // 2. Phản hồi kết nối thành công (ACK)
      // Chú ý: Chỉ bắt ACK có id là 2
      if (payload.result === null && payload.id === 2) {
          setConnStatus({ text: '🟢 LIVE (ĐÃ KẾT NỐI)', color: colors.green });
          return;
      }

      // 3. Bóc tách Data chuẩn cho cả cổng /ws và /stream
      const data = payload.data ? payload.data : payload;
      
      // Nếu không có event type (e), bỏ qua vì đây không phải luồng báo giá
      if (!data.e) return; 
      
      setLiveData(prev => {
        if (!prev) return null; // Chờ REST API lấy móng xong mới patch đè vào
        // Kiểm tra đúng Symbol mới patch (đề phòng delay đổi coin)
        if (data.s && data.s !== symbol.toUpperCase()) return prev;
        
        let updates = { ...prev };
        
        if (data.e === '24hrTicker') {
          updates.last = parseFloat(data.c); 
          updates.open = parseFloat(data.o);
          updates.high = parseFloat(data.h); 
          updates.low = parseFloat(data.l); 
          updates.changePct = parseFloat(data.P);
        } else if (data.e === 'kline') {
          const vol = parseFloat(data.k.q); 
          const takerBuy = parseFloat(data.k.Q);
          updates.vol = vol; 
          updates.cvd = takerBuy - (vol - takerBuy);
        } else if (data.e === 'markPriceUpdate') {
          updates.fundingRate = parseFloat(data.r);
        }

        if (updates.oiContract && updates.last) updates.oiQuote = updates.oiContract * updates.last;
        if (liveDataRef) liveDataRef.current = updates;
        return updates;
      });
    };

    const ws = wsRef.current;

    // Nếu đang có kết nối mà gọi startStream (thông thường đổi coin) -> Update luồng
    if (ws && ws.readyState === WebSocket.OPEN) {
      if (activeStreamsRef.current.length > 0) {
        // Thay Date.now() bằng id cố định: 2
        ws.send(JSON.stringify({ method: 'UNSUBSCRIBE', params: activeStreamsRef.current, id: 2 }));
      }
      ws.send(JSON.stringify({ method: 'SUBSCRIBE', params: streamsToSub, id: 2 }));
      
      activeStreamsRef.current = streamsToSub;
      ws.onmessage = handleMessage; 
    } else {
      // Dọn dẹp kết nối rác nếu có
      if (ws) ws.close(); 
      
      const newWs = new WebSocket('wss://fstream.binance.com/market/stream');
      
      newWs.onopen = () => {
        // Thay Date.now() bằng id cố định: 2
        newWs.send(JSON.stringify({ method: 'SUBSCRIBE', params: streamsToSub, id: 2 }));
        activeStreamsRef.current = streamsToSub;
      };

      newWs.onmessage = handleMessage;
      
      newWs.onerror = () => { 
        // Chỉ cảnh báo lỗi nếu đây vẫn là luồng hiện tại (chống đụng độ khi Reconnect)
        if (wsRef.current === newWs) {
          setConnStatus({ text: '🔴 LỖI MẠNG', color: colors.red }); 
        }
      };
      
      newWs.onclose = () => { 
        activeStreamsRef.current = []; 
        // Chỉ cảnh báo mất kết nối nếu đây là luồng hiện tại bị đứt ngẫu nhiên
        if (wsRef.current === newWs) {
          setConnStatus({ text: '🔴 MẤT KẾT NỐI (ĐÓNG)', color: colors.red });
        }
      };

      wsRef.current = newWs;
    }
  }, [symbol, liveDataRef]);

  const handleCheckSubscriptions = () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      setServerSubscriptions(['⏳ Đang lấy data từ Server...']);
      // Cố định id = 2 cho lệnh LIST_SUBSCRIPTIONS
      wsRef.current.send(JSON.stringify({ method: 'LIST_SUBSCRIPTIONS', id: 2 }));
    } else {
      setServerSubscriptions(['🔴 Chưa kết nối WebSocket']);
    }
  };

  const handleSaveSettings = () => {
    setDataSource(tempSource);
    setLiveData(null); setBaseline(null); baselineRef.current = null;
    setServerSubscriptions(null);
    if (tempSource !== 'stream') setConnStatus({ text: '⚪ ĐÃ NGẮT KẾT NỐI', color: colors.textSub });
  };

  // [CẬP NHẬT] Hàm chụp LiveData hiện tại làm Baseline mới
  const handleResetBaseline = () => {
    if (liveData) {
      setBaseline(liveData);
      baselineRef.current = liveData;
    }
  };

  useEffect(() => {
    setLiveData(null); setBaseline(null); baselineRef.current = null;
    setServerSubscriptions(null); 
    if (crawlerTimerRef.current) clearInterval(crawlerTimerRef.current);

    if (dataSource === 'stream') {
      fetchExtraData().then(() => {
        startStream();
      });
      
      const interval = setInterval(fetchExtraData, 15000); 

      // =========================================================
      // [CHỦ ĐỘNG RECONNECT]: Reset ống nước mỗi 10 phút (600,000ms)
      // =========================================================
      const reconnectInterval = setInterval(() => {
        console.log("♻️ Auto-reconnecting WebSocket sau 10 phút...");
        if (wsRef.current) {
          wsRef.current.close(); // Chặt đứt ống cũ
          wsRef.current = null;  // Xóa tham chiếu để startStream tự tạo ống mới tinh
        }
        startStream(); // Khởi tạo kết nối mới
      }, 600000); 

      return () => { 
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN && activeStreamsRef.current.length > 0) {
          // Hard code id = 2 khi Unmount
          wsRef.current.send(JSON.stringify({ method: 'UNSUBSCRIBE', params: activeStreamsRef.current, id: 2 }));
          activeStreamsRef.current = [];
        }
        clearInterval(interval); 
        clearInterval(reconnectInterval); // Dọn dẹp hẹn giờ reconnect
      };
    } else {
      fetchExtraData();
      crawlerTimerRef.current = setInterval(fetchExtraData, 15000);
      return () => clearInterval(crawlerTimerRef.current);
    }
  }, [symbol, dataSource, startStream, fetchExtraData]);

  useEffect(() => {
     // 1. Lính canh hệ điều hành: Chặn lúc người dùng bấm F5 hoặc Xóa Tab
    const handleBeforeUnload = () => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          // Hard code id = 2 khi Unmount
          wsRef.current.send(JSON.stringify({ method: 'UNSUBSCRIBE', params: activeStreamsRef.current, id: 2 }));
        }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      if (wsRef.current) wsRef.current.close();
    };
  }, []);

  if (!liveData || !baseline) return <div style={{...baseStyles.card, textAlign: 'center', padding: '40px'}}>⏳ Đang nạp dữ liệu {symbol}...</div>;

  return (
    <div style={{ marginBottom: '32px' }}>
      <div style={{...baseStyles.card, marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '10px 16px', border: `1px solid ${dataSource === 'stream' ? colors.green : colors.warn}`, flexWrap: 'wrap', gap: '12px'}}>
        
        {/* KHU VỰC CÀI ĐẶT */}
        <div style={{display: 'flex', flexDirection: 'column', gap: '10px'}}>
          <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
            <span style={{fontSize: '13px', fontWeight: 'bold', color: colors.textMain}}>DATA SOURCE:</span>
            <select value={tempSource} onChange={(e) => setTempSource(e.target.value)} style={baseStyles.select}>
              <option value="stream">⚡ LIVE STREAM</option>
              <option value="crawler">🐢 CRAWLER (15s)</option>
            </select>
            <button onClick={handleSaveSettings} style={{backgroundColor: colors.textMain, color: colors.bg, border: 'none', padding: '4px 12px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold'}}>SAVE</button>
          </div>
          
          {/* ĐÈN BÁO TRẠNG THÁI HIỆN TẠI */}
          <div style={{ 
            backgroundColor: 'rgba(255,255,255,0.05)', padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 'bold', display: 'inline-flex', alignItems: 'center', gap: '6px', width: 'fit-content',
            color: dataSource === 'stream' ? connStatus.color : colors.warn,
            border: `1px solid ${dataSource === 'stream' ? connStatus.color : colors.warn}`
          }}>
            {dataSource === 'stream' && connStatus.text.includes('LIVE') && (
              <div style={{ width: '6px', height: '6px', backgroundColor: colors.green, borderRadius: '50%', animation: 'pulse 1.5s infinite' }}></div>
            )}
            Trạng thái: {dataSource === 'stream' ? connStatus.text : '🐢 ĐANG DÙNG CRAWLER'}
          </div>
        </div>
        
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{color: dataSource === 'stream' ? colors.green : colors.warn, fontSize: '13px', fontWeight: 'bold'}}>
              {dataSource.toUpperCase()} | {symbol}
            </div>
            
            {/* [MỚI] NÚT RESET BASELINE */}
            <button
              onClick={handleResetBaseline}
              style={{ background: 'transparent', border: `1px solid ${colors.warn}`, color: colors.warn, padding: '4px 10px', borderRadius: '4px', fontSize: '11px', cursor: 'pointer', fontWeight: 'bold', transition: 'all 0.2s' }}
            >
              🔄 RESET BASELINE
            </button>

            {dataSource === 'stream' && (
              <button
                onClick={handleCheckSubscriptions}
                style={{ background: 'transparent', border: `1px solid ${colors.textMain}`, color: colors.textMain, padding: '4px 10px', borderRadius: '4px', fontSize: '11px', cursor: 'pointer', fontWeight: 'bold', transition: 'all 0.2s' }}
              >
                🔍 KIỂM TRA LUỒNG SÀN
              </button>
            )}

            <a
              href={`https://www.binance.com/en/futures/${symbol}`} target="_blank" rel="noopener noreferrer"
              style={{ backgroundColor: 'rgba(59, 130, 246, 0.1)', border: '1px solid #3b82f6', color: '#3b82f6', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold', display: 'inline-flex', alignItems: 'center', gap: '6px', textDecoration: 'none' }}
            >
              📈 XEM CHART
            </a>
          </div>
          
          {serverSubscriptions && dataSource === 'stream' && (
            <div style={{ fontSize: '10px', color: colors.green, backgroundColor: 'rgba(0,0,0,0.5)', border: `1px dashed ${colors.green}`, padding: '4px 8px', borderRadius: '4px', textAlign: 'right', maxWidth: '350px', wordWrap: 'break-word', marginTop: '2px' }}>
              <strong>Sàn phản hồi đang chạy:</strong><br/>
              {serverSubscriptions.length === 0 ? 'Trống (Chưa có luồng nào)' : serverSubscriptions.join(', ')}
            </div>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
        <div style={{ ...baseStyles.card, border: `1px solid ${colors.border}` }}>
          <div style={{ color: colors.textMain, fontWeight: 'bold', borderBottom: `1px solid ${colors.border}`, paddingBottom: '8px', marginBottom: '12px' }}>1️⃣ PRICE ACTION (GIÁ LIVE)</div>
          <div style={baseStyles.flexBetween}>
            <span style={{color: colors.textSub}}>GIÁ HIỆN TẠI:</span> 
            <strong style={{fontSize: '18px', color: liveData.last >= liveData.open ? colors.green : colors.red}}>${formatSmartPrice(liveData.last)}</strong>
          </div>
          <div style={baseStyles.flexBetween}>
            <span style={{color: colors.textSub}}>24H Change:</span> 
            <strong style={{color: liveData.changePct >= 0 ? colors.green : colors.red}}>{liveData.changePct > 0 ? '+':''}{liveData.changePct}%</strong>
          </div>
          <div style={baseStyles.flexBetween}>
            <span style={{color: colors.textSub}}>⚡ Delta vs Base:</span> 
            <DeltaBadge current={liveData.last} base={baseline.last} />
          </div>
          
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '16px', fontSize: '12px', color: colors.textSub, borderTop: `1px dashed ${colors.border}`, paddingTop: '10px' }}>
            <span>O: ${formatSmartPrice(liveData.open)}</span>
            <span>H: <strong style={{color: colors.green}}>${formatSmartPrice(liveData.high)}</strong></span>
            <span>L: <strong style={{color: colors.red}}>${formatSmartPrice(liveData.low)}</strong></span>
          </div>
        </div>

        <div style={{ ...baseStyles.card, border: `1px solid ${colors.border}` }}>
          <div style={{ color: colors.textMain, fontWeight: 'bold', borderBottom: `1px solid ${colors.border}`, paddingBottom: '8px', marginBottom: '12px' }}>2️⃣ LIQUIDITY & FUNDING</div>
          
          <div style={baseStyles.flexBetween}>
            <span style={{color: colors.textSub}}>OI CONTRACT:</span>
            <div style={{textAlign: 'right'}}><strong style={{fontSize: '16px'}}>{formatSmartPrice(liveData.oiContract)}</strong><br/><DeltaBadge current={liveData.oiContract} base={baseline.oiContract} /></div>
          </div>
        
          <div style={{...baseStyles.flexBetween, marginTop: '8px'}}>
            <span style={{color: colors.textSub}}>OI QUOTE (USDT):</span>
            <div style={{textAlign: 'right'}}><strong style={{fontSize: '16px', color: colors.textMain}}>{formatUSD(liveData.oiQuote)}</strong><br/><DeltaBadge current={liveData.oiQuote} base={baseline.oiQuote} /></div>
          </div>
        
          <div style={{...baseStyles.flexBetween, marginTop: '12px', borderTop: `1px dashed ${colors.border}`, paddingTop: '8px'}}>
            <span style={{color: colors.textSub}}>FUNDING RATE:</span>
            <div style={{textAlign: 'right'}}>
              <strong style={{ fontSize: '15px', color: liveData.fundingRate > 0.0001 ? colors.warn : (liveData.fundingRate < 0 ? colors.green : colors.textMain) }}>
                {liveData.fundingRate !== undefined ? `${liveData.fundingRate > 0 ? '+' : ''}${(liveData.fundingRate * 100).toFixed(4)}%` : '--'}
              </strong>
            </div>
          </div>
          
          <div style={{ fontSize: '10px', color: colors.textSub, marginTop: '4px', textAlign: 'right' }}>(Mốc Base: {formatUSD(baseline.oiQuote)})</div>
        </div>

        <div style={{ ...baseStyles.card, border: `1px solid ${colors.border}` }}>
          <div style={{ color: colors.textMain, fontWeight: 'bold', borderBottom: `1px solid ${colors.border}`, paddingBottom: '8px', marginBottom: '12px' }}>3️⃣ MOMENTUM (VOL 24H)</div>
          
          <div style={baseStyles.flexBetween}>
            <span style={{color: colors.textSub}}>TỔNG VOLUME:</span> 
            <span><strong>{formatUSD(liveData.vol)}</strong> <DeltaBadge current={liveData.vol} base={baseline.vol} /></span>
          </div>
        
          <div style={{ margin: '16px 0 8px 0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '6px' }}>
              <span style={{ color: colors.green, fontWeight: 'bold' }}>🟢 Buy: {((liveData.vol + liveData.cvd) / (2 * liveData.vol) * 100).toFixed(1)}%</span>
              <span style={{ color: colors.red, fontWeight: 'bold' }}>🔴 Sell: { (100 - ((liveData.vol + liveData.cvd) / (2 * liveData.vol) * 100)).toFixed(1)}%</span>
            </div>
            
            <div style={{ width: '100%', height: '8px', backgroundColor: colors.redBg, borderRadius: '4px', overflow: 'hidden', display: 'flex' }}>
              <div style={{ width: `${((liveData.vol + liveData.cvd) / (2 * liveData.vol) * 100)}%`, backgroundColor: colors.green, height: '100%', transition: 'width 0.5s ease-in-out' }} />
            </div>
          </div>
        
          <div style={{...baseStyles.flexBetween, marginTop: '12px'}}>
            <span style={{color: colors.textSub}}>CVD (ÁP LỰC RÒNG):</span>
            <strong style={{color: liveData.cvd >= 0 ? colors.green : colors.red}}>{liveData.cvd > 0 ? '+' : ''}{formatUSD(liveData.cvd)}</strong>
          </div>
          
          <div style={{ fontSize: '10px', color: colors.textSub, marginTop: '4px', textAlign: 'right' }}>(Net Flow: {liveData.cvd >= 0 ? 'Phe Mua thắng' : 'Phe Bán thắng'})</div>
        </div>
      </div>
      <LiquidationHeatmapAnalyzer symbol={symbol} liveDataRef={liveDataRef} smartMoneyRef={smartMoneyRef} />
      <MultiExchangeSentimentAnalyzer defaultSymbol={symbol} />
      <OrderBookAnalyzer defaultSymbol={symbol} />
      <HistoricalSection symbol={symbol} liveData={liveData} smartMoneyRef={smartMoneyRef} liveDataRef={liveDataRef} />
    </div>
  );
}
